/*  iceberg-ui  */
import { IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Tooltip, Typography } from '@mui/material';
import { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';

/*  Shared  */
import { FontSizes } from 'application/Shared/Constants';

/*  React  */
import React from 'react';
import { withTranslation } from 'react-i18next';

export enum TooltipPlacement {
    TOP = 'top',
    BOTTOM = 'bottom',
    LEFT = 'left',
    RIGHT = 'right'
}

export const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.tooltips.lightTooltip.background,
        boxShadow: theme.shadows[24],
        color: theme.palette.tooltips.lightTooltip.color
    },
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.tooltips.lightTooltip.arrowColor
    }
}));

/**
 * A styled tooltip component that displays content with dark theme styling.
 *
 * @component
 * @param {object} props - The component props
 * @param {string} props.className - The class name to be applied to the tooltip
 * @param {TooltipProps} props.props - Material-UI Tooltip props
 * @returns {JSX.Element} A styled Material-UI Tooltip component with dark theme
 *
 * @example
 * // Basic usage
 * <DarkTooltip title="Tooltip text">
 *   <Button>Hover me</Button>
 * </DarkTooltip>
 *
 * @example
 * // With arrow and placement
 * <DarkTooltip arrow placement="top" title="Tooltip text">
 *   <Button>Hover me</Button>
 * </DarkTooltip>
 *
 * @styled Uses Material-UI styled API for custom styling
 * @themeable Supports theme customization through theme.palette.tooltips.darkTooltip
 */
export const DarkTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.tooltips.darkTooltip.background,
        boxShadow: theme.shadows[1],
        color: theme.palette.tooltips.darkTooltip.color,
        fontSize: FontSizes[0]
    },
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.tooltips.darkTooltip.arrowColor
    }
}));

interface RenderContentProps {
    content: string | JSX.Element;
    size: string | number;
}

const RenderContent = ({ content, size }: RenderContentProps) => {
    if (content) {
        return content;
    }

    return (
        <IconWithVariant
            data-testid="helpIcon"
            variant="tooltipQ"
            src="QuestionMark"
            size={size}
            wrapperProps={{ marginLeft: '4px' }}
        />
    );
};

interface TooltipQProps {
    content?: string | JSX.Element;
    placement?: TooltipPlacement;
    size?: string | number;
    sx?: object;
    text: string;
}

export const TooltipQ = ({
    content = '',
    placement = TooltipPlacement.BOTTOM,
    size = FontSizes[1],
    sx = {},
    text
}: TooltipQProps) => (
    <DarkTooltip
        data-trackid="tooltipQRender"
        arrow
        placement={placement}
        title={<span>{text}</span>}
        sx={sx}
        className="no-print"
    >
        <Typography className="no-print" variant="span" display="inline-flex">
            <RenderContent content={content} size={size} />
        </Typography>
    </DarkTooltip>
);

export default withTranslation()(DarkTooltip);
